import React, { useState, useRef, useCallback, useEffect, useReducer } from 'react';
import ReactConsole from 'react-console-emulator';
import {Controlled as CodeMirror} from 'react-codemirror2';
console.log('CodeMirror loaded successfully');
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/mode/python/python';
console.log('Python mode loaded successfully');
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { FolderIcon, FileIcon, ChevronDownIcon, ChevronRightIcon, Play, 
         Trash2, GripHorizontal, Sun, MoonStarIcon, Plus, FolderPlus, Square } from 'lucide-react';
import getEditorSuggestions from './editor-suggestions';
import apiClient from "../../utils/apiClient.js";
import { createVMBridge } from '../../lib/vm-bridge';
import vm from 'scratch-vm';

// Constants
const MIN_EXPLORER_WIDTH = 200;
const MAX_EXPLORER_WIDTH = 600;
const MIN_TERMINAL_HEIGHT = 100;

// Initial state
const DEFAULT_PYTHON_CODE = `# Online Python compiler (interpreter) to run Python online.
# Write Python 3 code in this online editor and run it.
print("Stem World")`;

// State management with reducer
const initialState = {
  files: [{
    id: 'main',
    name: 'main.py',
    content: DEFAULT_PYTHON_CODE,
    isDefault: true,
    type: 'file'
  }],
  currentFile: 'main',
  code: DEFAULT_PYTHON_CODE,
  userInputs: [''],
  terminalHeight: 400,
  explorerWidth: 300,
  isDarkMode: true,
  isRunning: false,
  error: '',
  isResizing: false,
  isDragging: false,
  webGLContextLost: false,
  isAddingFile: false,
  newFileName: '',
  activeInput: null,  // Add this line
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_CODE':
      return {
        ...state,
        code: action.payload,
        files: state.files.map(file =>
          file.id === state.currentFile ? { ...file, content: action.payload } : file
        )
      };
    case 'SET_CURRENT_FILE':
      return {
        ...state,
        currentFile: action.payload,
        code: state.files.find(f => f.id === action.payload)?.content || state.code
      };
    case 'ADD_FILE':
      return {
        ...state,
        files: [...state.files, action.payload]
      };
    case 'DELETE_FILE':
      const newFiles = state.files.filter(f => f.id !== action.payload);
      const newCurrentFile = state.currentFile === action.payload ? 
        newFiles.find(f => f.isDefault)?.id : state.currentFile;
      return {
        ...state,
        files: newFiles,
        currentFile: newCurrentFile,
        code: newFiles.find(f => f.id === newCurrentFile)?.content || state.code
      };
    case 'UPDATE_DIMENSIONS':
      return {
        ...state,
        [action.payload.dimension]: action.payload.value
      };
    case 'TOGGLE_THEME':
      return {
        ...state,
        isDarkMode: !state.isDarkMode
      };
    case 'SET_RUNNING':
      return {
        ...state,
        isRunning: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };
    case 'HANDLE_WEBGL_CONTEXT':
      return {
        ...state,
        webGLContextLost: action.payload
      };
    case 'SET_IS_ADDING_FILE':
      return {
        ...state,
        isAddingFile: action.payload,
        newFileName: ''
      };
    
    case 'SET_NEW_FILENAME':
      return {
        ...state,
        newFileName: action.payload
      };
    
    case 'ADD_NEW_FILE':
      if (!state.newFileName.trim()) {
        return {
          ...state,
          error: 'File name cannot be empty'
        };
      }

      // Validate file extension
      const fileName = state.newFileName.endsWith('.py') 
        ? state.newFileName 
        : `${state.newFileName}.py`;

      // Check for duplicate names
      if (state.files.some(f => f.name === fileName)) {
        return {
          ...state,
          error: 'A file with this name already exists'
        };
      }

      const newFile = {
        id: `file-${Date.now()}`,
        name: fileName,
        content: '',
        type: 'file'
      };

      return {
        ...state,
        files: [...state.files, newFile],
        isAddingFile: false,
        newFileName: '',
        error: '',
        currentFile: newFile.id,
        code: ''
      };
    case 'SET_ACTIVE_INPUT':
      return {
        ...state,
        activeInput: action.payload
      };

    case 'SET_USER_INPUT':
      return {
        ...state,
        userInputs: state.userInputs.map((input, i) => 
          i === action.payload.index ? action.payload.value : input
        )
      };

    case 'ADD_USER_INPUT':
      return {
        ...state,
        userInputs: [...state.userInputs, ''],
        activeInput: state.userInputs.length
      };

    case 'DELETE_USER_INPUT':
      if (state.userInputs.length <= 1) {
        return {
          ...state,
          userInputs: [''],
          activeInput: null
        };
      }
      return {
        ...state,
        userInputs: state.userInputs.filter((_, i) => i !== action.payload),
        activeInput: null
      };

    case 'CLEAR_TERMINAL':
      return {
        ...state,
        error: ''  // Clear any error messages as well
      };

    default:
      return state;
  }
}

export default function PYTHONTab({ height = "100%", width = "100%", zIndex = 10, files, onFilesChange }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const consoleRef = useRef();
  const editorRef = useRef(null);
  const containerRef = useRef();
  const completionProviderRef = useRef(null);
  const [vmBridge] = useState(() => createVMBridge());

  // WebGL context loss handling
  useEffect(() => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.addEventListener('webglcontextlost', handleContextLost);
      canvas.addEventListener('webglcontextrestored', handleContextRestored);
    }
    return () => {
      if (canvas) {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      }
    };
  }, []);

  const handleContextLost = useCallback((event) => {
    event.preventDefault();
    dispatch({ type: 'HANDLE_WEBGL_CONTEXT', payload: true });
    // Attempt to restore context
    const canvas = event.target;
    setTimeout(() => {
      if (canvas.getContext) {
        const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        if (gl) {
          handleContextRestored();
        }
      }
    }, 300);
  }, []);

  const handleContextRestored = useCallback(() => {
    dispatch({ type: 'HANDLE_WEBGL_CONTEXT', payload: false });
  }, []);

  // Code execution with error handling
  const handleRunCode = useCallback(async () => {
    if (state.code.trim() === '' || state.isRunning) return;

    dispatch({ type: 'SET_RUNNING', payload: true });
    const executionId = Date.now();

    try {
      const controller = new AbortController();
      window.currentExecution = controller;

      // Send all files to backend for execution
      const filesPayload = state.files.map(f => ({ name: f.name, content: f.content }));
      const mainFile = state.files.find(f => f.id === state.currentFile)?.name || 'main.py';
      const response = await apiClient.post('/python/runCode', {
        files: filesPayload,
        main: mainFile,
        input: state.userInputs.join('\n'),
        executionId
      }, {
        signal: controller.signal,
        timeout: 300000
      });
      
      if (response.data) {
        consoleRef.current.pushToStdout(response.data.output);
      }
    } catch (error) {
      console.error('Execution error:', error);
      consoleRef.current.pushToStdout(`Error: ${error.message}\n`);
    } finally {
      dispatch({ type: 'SET_RUNNING', payload: false });
      delete window.currentExecution;
    }
  }, [state.code, state.isRunning, state.userInputs, state.files, state.currentFile]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
      }
      if (window.currentExecution) {
        window.currentExecution.abort();
      }
    };
  }, []);

  // --- Add refs to track drag start position and height ---
  const dragStartY = useRef(null);
  const dragStartHeight = useRef(null);

  // Add these mouse event handlers at the component level
  const handleMouseMove = useCallback((e) => {
    if (state.isResizing) {
      // Handling file explorer width resize
      const newWidth = e.clientX;
      if (newWidth >= MIN_EXPLORER_WIDTH && newWidth <= MAX_EXPLORER_WIDTH) {
        dispatch({
          type: 'UPDATE_DIMENSIONS',
          payload: { dimension: 'explorerWidth', value: newWidth }
        });
      }
    } else if (state.isDragging) {
      // Handling terminal height resize
      const container = containerRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      // Use dragStartY and dragStartHeight to calculate new height
      if (dragStartY.current !== null && dragStartHeight.current !== null) {
        const deltaY = dragStartY.current - e.clientY;
        let newHeight = dragStartHeight.current + deltaY;
        // Clamp
        if (newHeight < MIN_TERMINAL_HEIGHT) newHeight = MIN_TERMINAL_HEIGHT;
        if (newHeight > containerRect.height * 0.8) newHeight = containerRect.height * 0.8;
        dispatch({
          type: 'UPDATE_DIMENSIONS',
          payload: { dimension: 'terminalHeight', value: newHeight }
        });
      }
    }
  }, [state.isResizing, state.isDragging]);
  
  const handleMouseUp = useCallback(() => {
    dispatch({ type: 'UPDATE_DIMENSIONS', payload: { dimension: 'isResizing', value: false } });
    dispatch({ type: 'UPDATE_DIMENSIONS', payload: { dimension: 'isDragging', value: false } });
  }, []);
  
  // Add this useEffect to handle mouse events
  useEffect(() => {
    if (state.isResizing || state.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [state.isResizing, state.isDragging, handleMouseMove, handleMouseUp]);

  // ... Rest of the component implementation with updated state management
  // (File management, UI rendering, etc. following the same structure but using
  // the reducer for state updates)

  return (
    <div
      ref={containerRef}
      className={`rounded-lg overflow-hidden shadow-lg transition-colors duration-300 ${state.isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
        }`}
      style={{
        height: '78vh',
        width: "100%", // Changed from width prop to 100%
        zIndex,
        minHeight: '400px',
        position: 'relative'
      }}
    >
      <div className="flex h-full">
        {/* File Explorer */}
        <div
          style={{
            width: state.explorerWidth,
            minWidth: MIN_EXPLORER_WIDTH,
            maxWidth: MAX_EXPLORER_WIDTH
          }}
          className={`flex-shrink-0 border-r relative ${state.isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
            }`}
        >
          <div className="p-4 h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Project Files</h3>
              <div className="flex space-x-2">
                <Button
                  onClick={() => dispatch({ type: 'SET_IS_ADDING_FILE', payload: true })}
                  variant={state.isDarkMode ? "outline" : "secondary"}
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="New File"
                >
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {state.error && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {state.error}
              </div>
            )}

            {state.isAddingFile && (
              <div className="mb-4">
                <input
                  type="text"
                  value={state.newFileName}
                  onChange={(e) => dispatch({ 
                    type: 'SET_NEW_FILENAME', 
                    payload: e.target.value 
                  })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      dispatch({ type: 'ADD_NEW_FILE' });
                    }
                  }}
                  className={`w-full p-2 border rounded transition-colors duration-200 ${
                    state.isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="Enter file name (e.g., script.py)"
                  autoFocus
                />
                <div className="flex justify-end mt-2 space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => dispatch({ type: 'SET_IS_ADDING_FILE', payload: false })}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => dispatch({ type: 'ADD_NEW_FILE' })}
                  >
                    Create File
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              {state.files.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    if (item.type === 'file') {
                      dispatch({ type: 'SET_CURRENT_FILE', payload: item.id });
                    }
                  }}
                  className={`
                    relative group p-4 rounded-lg border transition-all duration-300
                    ${state.isDarkMode
                      ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }
                    ${item.type === 'file' ? 'cursor-pointer' : ''}
                    hover:shadow-lg hover:scale-102
                  `}
                >
                  <div className="flex items-center space-x-3">
                    {item.type === 'folder' ? (
                      <FolderIcon size={24} className={`${state.isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                    ) : (
                      <FileIcon size={24} className={`${state.isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
                    )}
                    <span className="truncate flex-1">{item.name}</span>
                    <Trash2
                      size={18}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:text-red-500 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch({ type: 'DELETE_FILE', payload: item.id });
                      }}
                    />
                  </div>

                  {item.type === 'file' && (
                    <div className="mt-2 text-xs text-gray-500">
                      Last modified: {new Date().toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}

              {/* Modules Section (30%) */}
              <div className="flex-[3] overflow-y-auto p-0 mt-[30vh]">
                <div className={`mb-4 rounded-lg border ${state.isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`p-3 ${state.isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-t-lg`}>
                    <h3 className={`text-sm font-semibold ${state.isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Modules
                    </h3>
                  </div>

                  <div className="p-2 space-y-2">
                    <label className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer transition-colors ${state.isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}>
                      <input
                        type="checkbox"
                        className={`form-checkbox h-4 w-4 rounded-sm border-2 ${state.isDarkMode ? 'border-gray-400 bg-gray-700 checked:bg-blue-400' : 'border-gray-500 bg-white checked:bg-blue-500'}`}
                      />
                      <span className={`text-sm ${state.isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        Stage
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resize Handle */}
          <div
            className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize ${state.isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              } hover:bg-blue-500 transition-colors duration-200 ${state.isResizing ? 'bg-blue-500' : ''
              }`}
            onMouseDown={(e) => dispatch({ type: 'UPDATE_DIMENSIONS', payload: { dimension: 'isResizing', value: true } })}
          />
        </div>
        {/* Main Editor Area */}
        <div className="flex-grow min-w-0 flex flex-col h-full">
          <div className={`flex justify-between items-center p-3 border-b ${state.isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
            }`}>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Label htmlFor="dark-mode" className={`text-sm ${state.isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                </Label>
                <div className="flex items-center space-x-2">
                  <Sun
                    size={18}
                    className={`transform transition-transform duration-300 hover:scale-125 hover:rotate-45 ${state.isDarkMode ? 'text-gray-400' : 'text-yellow-600'
                      }`}
                  />
                  <div
                    className={`relative w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${state.isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
                      }`}
                    onClick={() => dispatch({ type: 'TOGGLE_THEME' })}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-all duration-500 ${state.isDarkMode ? 'translate-x-6 rotate-[360deg]' : ''
                        }`}
                    ></div>
                  </div>
                  <MoonStarIcon
                    size={18}
                    className={`transform transition-transform duration-300 hover:scale-125 ${state.isDarkMode ? 'text-blue-700' : 'text-gray-400'
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CodeMirror Editor Full Height Wrapper */}
          <section>
            <div style={{ height: '100%', minHeight: 300 }}>
              <CodeMirror
                value={state.code || ''}
                options={{
                  mode: 'python',
                  theme: state.isDarkMode ? 'material' : 'default',
                  lineNumbers: true,
                  lineWrapping: true,
                  tabSize: 4,
                  indentUnit: 4,
                  smartIndent: true,
                  autofocus: true,
                  extraKeys: {
                    'Ctrl-Space': 'autocomplete',
                  },
                  readOnly: false, // Ensure editor is always editable
                }}
                onBeforeChange={(editor, data, value) => {
                  dispatch({ type: 'SET_CODE', payload: value || '' });
                }}
                className="w-full h-full"
              />
            </div>
          </section>

          <div className={`border-t p-2 flex flex-wrap items-center justify-between gap-2 ${state.isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-300'
            }`}>
            <div className="flex gap-2 flex-shrink-0">
  <Button
    onClick={handleRunCode}
    disabled={state.isRunning || state.code.trim() === ''}
    variant={state.isDarkMode ? "default" : "secondary"}
    size="sm"
    className="group relative overflow-hidden"
  >
    <div className="absolute inset-0 group-hover:bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    <Play
      size={16}
      className={`mr-1 transition-all duration-300 ${
        state.isRunning ? '' : 'group-hover:scale-110 group-hover:rotate-90'
      }`}
    />
    {state.isRunning ? 'Running...' : 'Run'}
  </Button>

  <Button
    onClick={() => dispatch({ type: 'SET_RUNNING', payload: false })}
    variant="destructive"
    size="sm"
    disabled={!state.isRunning}
    className={`group relative overflow-hidden ${
      state.isRunning ? 'opacity-100' : 'opacity-50'
    }`}
  >
    <div className="absolute inset-0 group-hover:bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    <Square
      size={16}
      className="mr-1 transition-all duration-300 group-hover:scale-110"
      fill="currentColor"
    />
    Stop
  </Button>

  <Button
    onClick={() => {
      if (consoleRef.current) {
        consoleRef.current.clearStdout();  // Clear the terminal output
        dispatch({ type: 'CLEAR_TERMINAL' });
      }
    }}
    variant={state.isDarkMode ? "destructive" : "outline"}
    size="sm"
    className="transform transition-transform duration-200 ease-out hover:-rotate-6 will-change-transform"
  >
    <Trash2 size={16} className="mr-1" />
    Clear
  </Button>
</div>

            <div className="flex-1 min-w-[200px] flex flex-wrap gap-2">
  {state.userInputs.map((input, index) => (
    <div
      key={index}
      className={`
        transition-all duration-300 ease-in-out
        ${state.isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}
        rounded-lg overflow-hidden
        hover:shadow-lg
        ${index === state.activeInput ? 'w-64' : 'w-24'}
      `}
    >
      <div
        className="flex items-center justify-between p-2 cursor-pointer"
        onClick={() => dispatch({ type: 'SET_ACTIVE_INPUT', payload: index === state.activeInput ? null : index })}
      >
        <span className={`text-sm ${state.isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {index === state.activeInput ? 'Input' : `#${index + 1}`}
        </span>
        <ChevronRightIcon
          size={16}
          className={`transform transition-transform duration-300 
            ${index === state.activeInput ? 'rotate-90' : ''} 
            ${state.isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
        />
      </div>
      
      <div className={`
        transition-all duration-300 ease-in-out
        ${index === state.activeInput ? 'h-auto opacity-100' : 'h-0 opacity-0'}
      `}>
        <div className="p-2 flex gap-2">
          <input
            value={input}
            onChange={(e) => dispatch({ type: 'SET_USER_INPUT', payload: { index, value: e.target.value } })}
            placeholder={`Value ${index + 1}`}
            className={`
              flex-1 px-2 py-1 rounded text-sm
              focus:outline-none
              ${state.isDarkMode
                ? 'bg-gray-600 text-gray-200 placeholder-gray-400'
                : 'bg-white text-gray-900 placeholder-gray-500 border border-gray-300'
              }
            `}
          />
          <Button
            onClick={() => dispatch({ type: 'DELETE_USER_INPUT', payload: index })}
            variant={state.isDarkMode ? "destructive" : "outline"}
            size="sm"
            className="px-2"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
    </div>
  ))}
  <Button
    onClick={() => dispatch({ type: 'ADD_USER_INPUT' })}
    variant={state.isDarkMode ? "outline" : "secondary"}
    size="sm"
    className="h-8 w-8 p-0 flex items-center justify-center"
  >
    <Plus size={16} />
  </Button>
</div>

          </div>

          <div
            className={`h-2 cursor-row-resize flex items-center justify-center border-t border-b select-none ${state.isDarkMode
              ? 'bg-gray-800 hover:bg-gray-700 border-gray-700'
              : 'bg-gray-200 hover:bg-gray-300 border-gray-300'
              } ${state.isDragging ? (state.isDarkMode ? 'bg-gray-700' : 'bg-gray-300') : ''}`}
            onMouseDown={(e) => {
              dragStartY.current = e.clientY;
              dragStartHeight.current = state.terminalHeight;
              dispatch({ type: 'UPDATE_DIMENSIONS', payload: { dimension: 'isDragging', value: true } });
            }}
            style={{ touchAction: 'none' }}
          >
            <GripHorizontal size={16} className={state.isDarkMode ? 'text-gray-500' : 'text-gray-600'} />
          </div>

          <div
            style={{ height: state.terminalHeight }}
            className={`border-t ${state.isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}
          >
            <ReactConsole
              commands={{
                run: {
                  description: 'Run the code in the editor',
                  usage: 'run',
                  fn: handleRunCode,
                },
                python: {
                  description: 'Execute a Python command',
                  usage: 'python <command>',
                  fn: async (...args) => {
                    const command = args.join(' ');
                    const filesPayload = state.files.map(f => ({ name: f.name, content: f.content }));
                    const mainFile = state.files.find(f => f.id === state.currentFile)?.name || 'main.py';
                    const response = await apiClient.post('/python/runCode', {
                      files: filesPayload,
                      main: mainFile,
                      code: command,
                      input: '',
                      executionId: Date.now(),
                      singleCommand: true
                    });
                    return response.data?.output || '';
                  },
                },
              }}
              noCommandFound="Command not found. Try 'python <command>' or 'run'"
              welcomeMessage="Welcome to the Python Terminal. Type 'help' for available commands."
              promptLabel=">>>"
              readOnly={false}
              autoFocus={false}
              ref={consoleRef}
              className={state.isDarkMode ? 'console-dark-mode' : 'console-light-mode'}
              style={{
                height: '100%',
                fontFamily: 'monospace',
                fontSize: '14px',
                padding: '1rem',
                overflowY: 'auto',
              }}
            />
          </div>
        </div>
      </div>
      <style>{`
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-x {
          animation: gradient-x 3s linear infinite;
          background-size: 200% 200%;
        }
        .CodeMirror-scroll::-webkit-scrollbar {
          display: none;
        }
        .CodeMirror-scroll {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;     /* Firefox */
        }
      `}</style>
    </div>
  )
}